import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { NotesTextEditor, NotesTextEditorSkeleton } from '@factorialco/f0-react/dist/experimental'
import { ClockBack, Cross, Delete, Pencil, Swap } from '@factorialco/f0-react/icons/app'
import {
  PagesPageCreateDraftDocument,
  PagesPageInfoDocument,
  PagesPageInfoItemFragment,
  PagesPageLastVersionDocument,
  PagesPageLastVersionItemFragment,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DeletePageDialog } from 'modules/pages/components/DeletePageDialog'
import { MovePageModal } from 'modules/pages/components/MovePageModal'
import { PageErrorLayout } from 'modules/pages/components/PageErrorLayout'
import { useNotesTextEditor } from 'modules/pages/lib/hooks/useNotesTextEditor'
import { usePageAcknowledgement } from 'modules/pages/lib/hooks/usePageAcknowledgement'
import { usePageAutosave } from 'modules/pages/lib/hooks/usePageAutosave'
import { usePagesContext } from 'modules/pages/lib/hooks/usePagesContext'
import { VersionHistoryModal } from 'modules/pages/pages/Page/components/VersionHistoryModal'

import { AcknowledgementBar } from './components/AcknowledgementBar'
import { DiscardPageDraftDialog } from './components/DiscardPageDraftDialog'
import { PublishPageDialog } from './components/PublishPageDialog'
import * as styles from './index.vanilla.css'

interface UsePageMetadataProps {
  page?: PagesPageInfoItemFragment
  lastVersion?: PagesPageLastVersionItemFragment
  canEditPages: boolean
  isUpdating: boolean
  hasAcknowledged?: boolean
  isLoadingAcknowledgement?: boolean
}

const usePageMetadata = ({
  page,
  lastVersion,
  isUpdating,
  hasAcknowledged,
  isLoadingAcknowledgement,
}: UsePageMetadataProps) => {
  const { canEditPages } = usePagesContext()

  const owner = useMemo(() => {
    const targetEmployee = !lastVersion ? page?.owner : lastVersion?.createdByEmployee

    return {
      type: 'person' as const,
      label: i18n.t('pages.page.owner'),
      firstName: targetEmployee?.firstName ?? '',
      lastName: targetEmployee?.lastName ?? '',
      src: buildUrl(targetEmployee?.avatar?.url),
    }
  }, [lastVersion, page])

  const readRequiredStatus = useMemo(() => {
    if (!page?.mandatory) return undefined
    // Don't show loading state, just show the appropriate badge
    if (isLoadingAcknowledgement) return undefined

    if (hasAcknowledged) {
      return {
        type: 'status' as const,
        label: i18n.t('pages.page.read'),
        variant: 'positive' as const,
      }
    }
    return {
      type: 'status' as const,
      label: i18n.t('pages.page.read_required'),
      variant: 'warning' as const,
    }
  }, [page?.mandatory, hasAcknowledged, isLoadingAcknowledgement])

  const publishedAt = useMemo(() => {
    if (canEditPages && (page?.visibility === 'private' || page?.status === 'draft')) {
      return {
        type: 'text' as const,
        label: i18n.t('pages.page.last_updated'),
        content: isUpdating
          ? i18n.t('pages.page.saving')
          : dateBackendDateTimeToRelativeTime(page?.updatedAt ?? ''),
      }
    }
    return {
      type: 'text' as const,
      label: i18n.t('pages.page.last_updated'),
      content: dateBackendDateTimeToRelativeTime(page?.lastPublishedAt),
    }
  }, [page, isUpdating, canEditPages])

  const status = useMemo(() => {
    if (!canEditPages) return undefined
    return {
      type: 'status' as const,
      variant:
        page?.visibility === 'private' || page?.status === 'draft'
          ? ('neutral' as const)
          : ('info' as const),
      label:
        page?.visibility === 'private' || page?.status === 'draft'
          ? i18n.t('pages.page.draft')
          : i18n.t('pages.page.public'),
    }
  }, [canEditPages, page?.visibility, page?.status])

  return useMemo(
    () => [status, readRequiredStatus, owner, publishedAt].filter(Boolean),
    [status, owner, readRequiredStatus, publishedAt]
  )
}

const Page = () => {
  const { pageId, folderSlug } = useParams<{ pageId: string; folderSlug: string }>()
  const { toolbarLabels, titlePlaceholder, placeholder } = useNotesTextEditor()
  const [searchParams, setSearchParams] = useSearchParams()
  const { canEditPages } = usePagesContext()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openMovePageModal, setOpenMovePageModal] = useState(false)
  const [openPublishPageDialog, setOpenPublishPageDialog] = useState(false)
  const [openDiscardPageDraftDialog, setOpenDiscardPageDraftDialog] = useState(false)
  const [openVersionHistoryModal, setOpenVersionHistoryModal] = useState(false)

  const { debouncedAutosave, isUpdating } = usePageAutosave({ pageId: Number(pageId) })

  const { loading, error, data } = useQuery(PagesPageInfoDocument, {
    variables: {
      id: Number(pageId),
    },
    skip: !pageId,
  })

  const {
    loading: loadingLastVersion,
    error: errorLastVersion,
    data: dataLastVersion,
  } = useQuery(PagesPageLastVersionDocument, {
    variables: {
      pageId: Number(pageId),
    },
    skip: !pageId,
  })

  const page = data?.handbookPages.page ?? undefined
  const lastVersion = dataLastVersion?.handbookPages.pageVersionsConnection.nodes[0] ?? undefined

  // Check if user needs to acknowledge this page
  const showAcknowledgementBar = page?.mandatory && page?.visibility !== 'private'
  const acknowledgement = usePageAcknowledgement({
    pageId: Number(pageId),
    ackRequiredVersion: page?.ackRequiredVersion,
    isMandatory: showAcknowledgementBar,
  })

  const [createDraft] = useMutation(PagesPageCreateDraftDocument, {
    extractorFn: (result) => result.handbookPagesMutations.updateHandbookpagesPage,
  })

  const handleOpenPublishDialog = () => {
    track(trackingEvents.pages.PagesPublishNewPage, {
      folder_type: folderSlug ?? '',
      page_name: page?.title ?? '',
    })
    setOpenPublishPageDialog(true)
  }

  const handleEditPage = () => {
    track(trackingEvents.pages.PagesEditPage, {
      folder_type: folderSlug ?? '',
      page_name: page?.title ?? '',
    })
    createDraft({ variables: { id: Number(pageId) } })
  }

  const applyBlockHighlight = useCallback(
    (targetBlock: HTMLElement) => {
      targetBlock.classList.add(styles.blockHighlight)
      setTimeout(() => {
        targetBlock.classList.remove(styles.blockHighlight)
        // Remove block parameter from URL
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('block')
        setSearchParams(newSearchParams, { replace: true })
      }, 1500)
    },
    [searchParams, setSearchParams]
  )

  useEffect(() => {
    if (!page || loading) return

    const blockId = searchParams.get('block')
    if (!blockId) return

    const timeoutId = setTimeout(() => {
      const targetBlock = document.querySelector<HTMLElement>(`[data-id="${blockId}"]`)

      if (targetBlock) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                observer.disconnect()
                applyBlockHighlight(targetBlock)
              }
            })
          },
          { threshold: 0.5 }
        )

        observer.observe(targetBlock)
        targetBlock.scrollIntoView({ behavior: 'smooth', block: 'center' })

        return () => observer.disconnect()
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [page, loading, searchParams, applyBlockHighlight])

  const initialContent = useMemo(() => {
    if (canEditPages) {
      return page?.content && page?.content.trim() !== '' ? JSON.parse(page.content) : undefined
    }
    return lastVersion?.content && lastVersion?.content.trim() !== ''
      ? JSON.parse(lastVersion.content)
      : undefined
  }, [page, lastVersion, canEditPages])

  const intialTitle = useMemo(() => {
    if (canEditPages) {
      return page?.title
    }
    return lastVersion?.title
  }, [page, lastVersion, canEditPages])

  const metadata = usePageMetadata({
    page,
    lastVersion,
    canEditPages,
    isUpdating,
    hasAcknowledged: acknowledgement.hasAcknowledged,
    isLoadingAcknowledgement: acknowledgement.loading,
  })

  const editorConfig = {
    labels: { toolbarLabels, titlePlaceholder },
    placeholder,
    initialEditorState: {
      content: initialContent,
      title: intialTitle,
    },
    metadata,
    secondaryActions: canEditPages
      ? [
          page?.visibility !== 'private' && {
            label: i18n.t('pages.page.version_history'),
            onClick: () => setOpenVersionHistoryModal(true),
            icon: ClockBack,
          },
          {
            label: i18n.t('pages.page.move_page.title'),
            icon: Swap,
            onClick: () => setOpenMovePageModal(true),
          },
          {
            label: i18n.t('pages.page.delete'),
            icon: Delete,
            onClick: () => setOpenDeleteDialog(true),
            critical: true,
          },
        ].filter(Boolean)
      : undefined,
  }

  const editMode = useMemo(() => {
    if (!canEditPages) return false
    if (page?.visibility === 'private') return true
    if (page?.status === 'draft') return true
    return false
  }, [canEditPages, page?.visibility, page?.status])

  if ((!page || error || errorLastVersion) && !loading && !loadingLastVersion) {
    return <PageErrorLayout />
  }

  return (
    <>
      <Box height="full" flexDirection="column" overflowY="hidden">
        <Box flexGrow overflowY="hidden">
          {loading || loadingLastVersion ? (
            <NotesTextEditorSkeleton withPadding withHeader withTitle withToolbar={false} />
          ) : editMode ? (
            <NotesTextEditor
              key={`editable-editor-${pageId}`}
              {...editorConfig}
              onChange={({ json }) => {
                const jsonString = JSON.stringify(json)
                debouncedAutosave(jsonString, page?.title ?? '')
              }}
              onTitleChange={(title) => {
                if (title !== page?.title && title.trim() !== '') {
                  debouncedAutosave(page?.content ?? '', title)
                }
              }}
              actions={
                canEditPages
                  ? [
                      page?.status === 'draft' &&
                        page?.visibility !== 'private' && {
                          label: i18n.t('pages.page.close_edit'),
                          onClick: () => setOpenDiscardPageDraftDialog(true),
                          icon: Cross,
                        },
                      {
                        label: i18n.t('pages.page.publish'),
                        onClick: handleOpenPublishDialog,
                        variant: 'default' as const,
                      },
                    ].filter(Boolean)
                  : undefined
              }
              readonly={!canEditPages}
            />
          ) : (
            <NotesTextEditor
              key={`readonly-editor-${pageId}`}
              {...editorConfig}
              onChange={() => {}}
              readonly
              actions={
                canEditPages
                  ? [
                      {
                        label: i18n.t('pages.page.edit'),
                        onClick: handleEditPage,
                        icon: Pencil,
                      },
                    ]
                  : undefined
              }
            />
          )}
        </Box>

        {showAcknowledgementBar && (
          <AcknowledgementBar
            loading={acknowledgement.loading}
            acknowledging={acknowledgement.acknowledging}
            hasAcknowledged={acknowledgement.hasAcknowledged}
            pageAckVersion={acknowledgement.pageAckVersion}
            onAcknowledge={acknowledgement.handleAcknowledge}
            isEditing={editMode}
          />
        )}
      </Box>

      {openDeleteDialog && folderSlug && (
        <DeletePageDialog
          pageId={Number(pageId)}
          onClose={() => setOpenDeleteDialog(false)}
          folderType={folderSlug}
          redirect
        />
      )}
      {openMovePageModal && folderSlug && (
        <MovePageModal
          pageId={Number(pageId)}
          onClose={() => setOpenMovePageModal(false)}
          initialFolderType={folderSlug}
          redirectInPage
        />
      )}
      {openPublishPageDialog && (
        <PublishPageDialog
          pageId={Number(pageId)}
          folderType={folderSlug ?? ''}
          pageName={page?.title ?? ''}
          isMandatory={page?.mandatory ?? false}
          onClose={() => setOpenPublishPageDialog(false)}
          hasContent={!!page?.content && page.content.trim() !== ''}
          hasTitle={!!page?.title && page.title.trim() !== ''}
        />
      )}
      {openDiscardPageDraftDialog && (
        <DiscardPageDraftDialog
          pageId={Number(pageId)}
          lastVersionContent={lastVersion?.content ?? ''}
          onClose={() => setOpenDiscardPageDraftDialog(false)}
        />
      )}
      {openVersionHistoryModal && (
        <VersionHistoryModal
          isOpen={openVersionHistoryModal}
          onClose={() => setOpenVersionHistoryModal(false)}
          pageId={Number(pageId)}
        />
      )}
    </>
  )
}

export default Page
